import { useEffect, useState } from "react";
import { authAxios } from "../utils/auth";

/**
 * Poll the Guardian threat summary every 60s so the Admin Shield icon
 * can tint itself against the current alert code (GREEN/AMBER/RED/HIGH_ALERT).
 *
 * Deliberately in-memory only — no localStorage. The 60s cadence is
 * cheap on the backend (a handful of aggregations against small collections).
 */
export function useGuardianStatus({ enabled = true, intervalMs = 60000 } = {}) {
  const [state, setState] = useState({
    code: "GREEN",
    headline: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let mounted = true;

    const load = async () => {
      try {
        const r = await authAxios.get("/admin/fraud/threat-summary");
        if (!mounted) return;
        setState({
          code: r.data?.code || "GREEN",
          headline: r.data?.headline || null,
          reasons: r.data?.reasons || [],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (!mounted) return;
        setState((s) => ({ ...s, loading: false, error: err }));
      }
    };

    load();
    const id = setInterval(load, intervalMs);
    return () => { mounted = false; clearInterval(id); };
  }, [enabled, intervalMs]);

  return state;
}

/**
 * Poll the current user's own account_status. Used by NoticeBlock to
 * render the warning/frozen banner and by BlockedPage to detect
 * reactivation without a manual reload.
 */
export function useMeStatus({ enabled = true, intervalMs = 45000 } = {}) {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    if (!enabled) return undefined;
    let mounted = true;
    const load = async () => {
      try {
        const r = await authAxios.get("/me/status");
        if (mounted) setState({ loading: false, data: r.data });
      } catch {
        if (mounted) setState({ loading: false, data: null });
      }
    };
    load();
    const id = setInterval(load, intervalMs);
    return () => { mounted = false; clearInterval(id); };
  }, [enabled, intervalMs]);

  return state;
}
